import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";

export const NotificationsContext = createContext({
  notifications: [],
  unreadCount: 0,
  modalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  addNotification: () => {},
  removeNotification: () => {},
  markAllRead: () => {},
  clearAll: () => {},
});

const STORAGE_KEY_BASE = "relay.notifications.v1";
const keyForUser = (userId) => `${STORAGE_KEY_BASE}.${userId || "anon"}`;

export default function NotificationsProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        return u.userId || u.id || u.sub || null;
      }
    } catch {}
    return null;
  });

  const reduxUser = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (currentUserId) return;
    const uid = reduxUser?.userId || reduxUser?.id || reduxUser?.sub || null;
    if (uid) setCurrentUserId(uid);
  }, [currentUserId, reduxUser]);

  // Load notifications for this user when we know their id
  useEffect(() => {
    if (!currentUserId) return;
    try {
      const legacy = localStorage.getItem(STORAGE_KEY_BASE);
      const userKey = keyForUser(currentUserId);
      const existing = localStorage.getItem(userKey);
      const raw = existing || legacy;
      setNotifications(raw ? JSON.parse(raw) : []);
    } catch {
      setNotifications([]);
    }
  }, [currentUserId]);

// Fetch from server as source of truth once user is known
  // REMOVED: Server fetch is disabled as requested

  // Lightweight polling to refresh notifications from server
  // REMOVED: Server polling is disabled as requested

  useEffect(() => {
    try {
      if (!currentUserId) return;
      localStorage.setItem(
        keyForUser(currentUserId),
        JSON.stringify(notifications)
      );
    } catch {}
  }, [notifications, currentUserId]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const addNotification = useCallback(
    async (notification) => {
      // Only show to the intended user if target specified
      const targetId =
        notification.targetUserId ||
        notification.meta?.assignedToId ||
        notification.meta?.assigneeId;
      if (targetId && !currentUserId) {
        // Current user unknown yet; avoid showing mis-targeted items
        return;
      }
      if (
        targetId &&
        currentUserId &&
        String(targetId) !== String(currentUserId)
      ) {
        return; // Not for this user
      }

      const localId =
        notification.id ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const createdAt =
        notification.createdAt ||
        new Date().toISOString();
      setNotifications((prev) => [
        {
          id: localId,
          type: notification.type || "info",
          title: notification.title || "Notification",
          message: notification.message || "",
          createdAt,
          read: false,
          meta: notification.meta || {},
        },
        ...prev,
      ]);
    },
    [currentUserId]
  );

  const removeNotification = useCallback(async (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(async () => {
    setNotifications([]);
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      modalOpen,
      openModal,
      closeModal,
      addNotification,
      removeNotification,
      markAllRead,
      clearAll,
    }),
    [
      notifications,
      unreadCount,
      modalOpen,
      openModal,
      closeModal,
      addNotification,
      removeNotification,
      markAllRead,
      clearAll,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
